import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, type CSSProperties, useEffect, useMemo, useRef } from "react";

import type { Corridor, CorridorModel, PhaseLine } from "../../api/types";
import type { LineName } from "../manoeuvre/corridorEdit";
import { useUi } from "../../state/ui";
import { setDispositifPreview } from "../dossierWrites";
import {
  blendShapes,
  type CorridorShape,
  LIMA_OVERSHOOT_EDIT_PX,
  LIMA_OVERSHOOT_PX,
  limaOvershootRatio,
  labelPlacement,
  labelTransform,
  lineLabels,
  modelHours,
  overshootLines,
  overshootZoom,
  sameShape,
  shapeFromModel,
  type ShapeFrame,
} from "../manoeuvre/corridorDisplay";
import { useGestureState } from "../manoeuvre/gestureState";
import { EMPTY_LIMA_GEOMETRY, stackLimaChips, useLimaGeometry } from "../manoeuvre/limaChips";
import { canAnimate, useEased, useTicker } from "../manoeuvre/motion";
import { t } from "../../i18n/i18n";
import { useOpenZone } from "../zones/useZones";
import { useManoeuvre } from "../manoeuvre/useManoeuvre";
import { DATA_TRANSITION_MS, smoothstep } from "./phaseTransition";
import {
  arcPosition,
  closestPoint,
  type LonLat,
  labelLast,
  limaCrossing,
  moveM,
  pointAlongPolyline,
  railCross,
  trackPosition,
} from "../geo";
import { formatDtg } from "../dtg";
import { lineLabel as lineLabelIn, useDossierLanguage } from "../lineNames";
import { REVEAL_MS, revealPaint, revealStyle, useMapRevealed } from "../reveal";
import { FRIEND_BLUE } from "./symbols";
import { useOrientation, useShownDeployment } from "../manoeuvre/useOrientation";

/** Mesures de coordination du dispositif, dessinées comme sur la carte de l'iPad (MapLibreMapView) :
 *  limites du fuseau et lignes L0 à L3 adaptées au terrain par le serveur, en bleu des unités amies, épaisseur 3,
 *  trait plein ; un symbole d'échelon (II, X, XX) par limite, en son milieu ; une étiquette par ligne, posée
 *  au-delà de l'extrémité côté étiquette, dans l'axe de la ligne. Couches sous les symboles d'unités ;
 *  identifiants « corridor-… ».
 *  Deux étiquettes ne se recouvrent jamais (`stackLimaChips`) : celle qui toucherait une étiquette déjà posée, une
 *  marque d'échelon, une limite ou une autre LIMA s'écarte vers l'extérieur dans l'axe de sa ligne, du minimum qui la
 *  dégage plus 4 px, recalculé au quart de niveau de zoom et au cap de la carte. La géométrie affichée (étiquettes,
 *  LIMA prolongées, limites, marques) est publiée pour les lignes de la ZO (`useLimaGeometry`).
 *  Chaque LIMA dépasse de part et d'autre des limites : les données portent 500 m, et le tracé dessiné est
 *  prolongé si besoin pour que ce dépassement fasse au moins 12 px à l'écran (20 px en édition, au-delà de la
 *  poignée d'extrémité et de son halo), recalculé à chaque quart de niveau de zoom (affichage seulement) ; hors
 *  édition, ce prolongement ne passe pas 5 % de la largeur locale du fuseau (4 px au moins), si bien qu'un fuseau vu
 *  de loin, camp ou terrain libre, garde l'aspect d'un camp à son zoom d'entrée (demandes d'Alexis du 04/10/2026).
 *  L'étiquette suit la charte web (puce de l'interface : police, jetons de couleur, bordure fine) et non la
 *  boîte blanche fixe de 200 × 28 de l'iPad, bien trop large autour de « L0 ».
 *
 *  Manœuvre libre (docs/14 §2.3, §2.10) : pendant un geste (`useManoeuvre.working`) ou plusieurs annulations
 *  rapides (aperçu de l'historique), le fuseau et ses LIMA sont dessinés depuis le modèle éditable, avec les GDH
 *  d'aperçu (règle K3) ; l'aperçu reste jusqu'à la réponse (B28). Quand le fuseau est remplacé (retour au fuseau
 *  automatique, annulation, réponse du serveur), la géométrie affichée glisse sommet par sommet vers la nouvelle
 *  si les nombres de points sont égaux, sinon les deux se fondent l'une dans l'autre (900 ms). */

export const LINE_HIT_LAYER_ID = "corridor-lines-hit";

// Bleu des unités amies de l'iPad (blueGenerex) : limites, symbole d'échelon et LIMA le portent aussi.
const CONTROL_COLOR = FRIEND_BLUE;
const LIMA_COLOR = FRIEND_BLUE;
const LINE_WIDTH = 3; // iPad : largeur constante, trait plein, opacité 1
const FONT = ["Open Sans Semibold"];
/** Symbole d'échelon (II, X, XX) posé sur chaque limite. */
const ECHELON_LAYOUT = {
  "text-field": ["get", "text"],
  "text-font": FONT,
  "text-size": 28,
  "text-rotate": ["get", "rotation"],
  "text-rotation-alignment": "map",
  "text-allow-overlap": true,
  "text-ignore-placement": true,
};

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
  /** Zoom courant : dépassement des LIMA à l'écran. */
  zoom: number;
  /** Cap de la carte (°) : place des étiquettes. */
  bearing?: number;
}

/** Étiquette d'une LIMA : puce de l'interface (comme les badges), nom en bleu ami, GDH en chasse fixe comme
 *  dans la fiche de ligne ; police de l'interface imposée (le conteneur du moteur de carte impose Helvetica).
 *  Transparente aux gestes : la ligne se saisit par sa couche de préhension. */
function LineTag({ name, detail, tag }: { name: string; detail: string | null; tag?: string }) {
  return (
    <div
      data-line-tag={tag}
      className="pointer-events-none flex h-5 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
      <span className="text-[12px] leading-none font-semibold text-friend">{name}</span>
      {detail && <span className="font-mono text-[11px] leading-none text-ink-soft tabular-nums">{detail}</span>}
    </div>
  );
}

/** Aperçu d'une ligne glissée jusqu'à l'abscisse `position` : la barre qui joint les deux limites, comme le
 *  serveur (limaCrossing, ou barre accrochée au camp en CENTAC), avant l'adaptation au terrain qu'il fait au
 *  relâcher. Tracé orienté pour finir à l'extrémité de l'étiquette. */
export function lineGeometry(corridor: Corridor, position: number): [number, number][] {
  const { track, upper, lower, line_margin_m } = corridor;
  const clamped = Math.min(Math.max(position, track.low_m), track.high_m);
  let bar: [LonLat, LonLat, LonLat] | null;
  if (track.kind === "axis") {
    bar = railCross(upper, lower, moveM(track.origin, track.axis, clamped), line_margin_m);
  } else {
    const point = pointAlongPolyline(upper, clamped) ?? upper[upper.length - 1]!;
    bar = limaCrossing(point, upper, lower, line_margin_m);
  }
  return bar ? labelLast(bar, lower) : [];
}

/** Abscisse d'un point sur le repère des glissements, comme le serveur : abscisse curviligne du point le plus
 *  proche sur la limite de référence (hors CENTAC), projection sur l'axe du camp (CENTAC). */
export function trackMeasure(corridor: Corridor, point: LonLat): number {
  const { track } = corridor;
  return track.kind === "axis" ? trackPosition(track.origin, track.axis, point) : arcPosition(corridor.upper, point);
}

/** Échantillonnage des barres pour `slideMeasure` : une barre tous les 20 m de la limite de référence, entre 200 et
 *  4 000 barres. */
const SLIDE_STEP_M = 20;
const SLIDE_SAMPLES_MIN = 200;
const SLIDE_SAMPLES_MAX = 4000;
/** Barres à égalité sous le pointeur (`slideMeasure`) : LIMA prolongée qui passe à moins de 10 m (plus un dixième de
 *  l'écart) de plus que la plus proche ; entre elles, la continuité départage. */
const SLIDE_TIE_M = 10;
const RAD = Math.PI / 180;
const M_PER_DEG = 111_320;

/** Mesure du glissement d'une LIMA du fuseau automatique (camp ou terrain libre, hors CENTAC), pointeur → abscisse
 *  du repère des glissements. La LIMA glissée est dessinée comme le serveur la posera (`lineGeometry` : barre du
 *  point de la limite de référence à l'abscisse donnée au point le plus proche de l'autre limite, prolongée de
 *  `line_margin_m` de chaque côté) ; la mesure rend l'abscisse dont cette barre passe sous le pointeur, si bien que
 *  la LIMA reste sous le doigt partout où une barre peut y passer, sur son prolongement hors des limites compris
 *  (revue du 04/10/2026 : ni le point le plus proche de la limite de référence, qui se figeait derrière un coin
 *  rentrant puis sautait, ni le repère de l'amorce, qui coupe les coins et laissait la LIMA à des kilomètres du
 *  doigt sur une amorce en éventail ; docs/14 §2.4).
 *  - Une barre tous les 20 m ; le pointeur situé entre deux barres voisines (côté de chacune, orienté dans le sens
 *    du fuseau) donne l'abscisse par interpolation, affinée par fausse position sur la barre du serveur : abscisse
 *    croissante avec le pointeur, sans plateau ni saut, là où les barres balaient le fuseau (pivot autour d'un coin
 *    de l'autre limite compris).
 *  - Sens des barres : un seul pour tout le fuseau (normale à gauche ou à droite de la barre, de la limite de
 *    référence vers l'autre), celui où avance le pied sur la limite de référence. La normale tourne avec la barre,
 *    d'un saut de plus de 90° compris (coude marqué du fuseau).
 *  - Là où la barre du serveur saute (le point le plus proche de l'autre limite change de tronçon), le triangle
 *    laissé entre les deux barres du saut n'est couvert par aucune : la LIMA reste sur la barre la plus proche de
 *    part et d'autre et franchit le saut quand le doigt passe à égale distance des deux.
 *  - Choix de la barre : chaque candidate est jugée sur la distance de sa LIMA prolongée au pointeur (une barre
 *    lointaine dont seule la droite passe par le pointeur ne compte pas). Si une barre passe sous le pointeur (à
 *    10 m près) : seules celles qui y passent concourent (croisées ; la plus proche sur la branche de la dernière
 *    abscisse si elle passe à moins d'1 m, autour d'un pivot ; à la saisie, la LIMA elle-même si elle passe à moins
 *    d'1 m). Sinon (triangle d'un saut, au-delà des prolongements, éventail de barres presque aussi proches) : aussi
 *    la barre la plus proche et la plus proche sur la branche de la dernière abscisse. Parmi celles qui passent au
 *    plus près, à 10 m plus un dixième de l'écart près, la plus proche de la dernière abscisse rendue (`hint` à la
 *    saisie : la position de la LIMA) : la LIMA ne change pas de branche sous le doigt (limite de référence qui
 *    part de côté ou rebrousse chemin, prolongements qui se croisent hors des limites). Seul un point où plusieurs
 *    LIMA distinctes passent exactement (pivot commun à deux branches) laisse la branche se décider quand le doigt
 *    en sort.
 *  Le geste reste relatif (position de départ + écart depuis la saisie, B28) ; le point envoyé au relâcher est celui
 *  de la limite de référence à l'abscisse retenue, le serveur y retrouve la position de l'aperçu et range le
 *  glissement dans les glissements automatiques (fuseau toujours automatique). Sans modèle (mode iPad) ou en
 *  CENTAC : `trackMeasure`. */
export function slideMeasure(corridor: Corridor, hint?: number): (point: LonLat) => number {
  const fallback = (point: LonLat) => trackMeasure(corridor, point);
  const { track, upper, lower, model } = corridor;
  const length = track.high_m - track.low_m;
  if (track.kind === "axis" || !model || upper.length < 2 || lower.length < 2 || !(length > 0)) return fallback;
  // Prolongement de la LIMA au-delà de chaque limite (données du serveur, 500 m).
  const margin = Number.isFinite(corridor.line_margin_m) ? Math.max(corridor.line_margin_m, 0) : 500;
  const n = Math.min(Math.max(Math.ceil(length / SLIDE_STEP_M), SLIDE_SAMPLES_MIN), SLIDE_SAMPLES_MAX);
  const step = length / n;
  // Plan local unique (équirectangulaire au barycentre des limites) : côtés, rapports et distances de quelques
  // mètres y sont lus.
  let lat0 = 0;
  let lon0 = 0;
  for (const p of [...upper, ...lower]) [lon0, lat0] = [lon0 + p[0], lat0 + p[1]];
  [lon0, lat0] = [lon0 / (upper.length + lower.length), lat0 / (upper.length + lower.length)];
  const mx = M_PER_DEG * Math.cos(lat0 * RAD);
  const xy = (p: LonLat): [number, number] => [(p[0] - lon0) * mx, (p[1] - lat0) * M_PER_DEG];
  // Barre k : la LIMA dessinée (`limaCrossing`, sans son prolongement), de la limite de référence à l'autre. Elle
  // part du milieu des pieds R (limite de référence) et L (point le plus proche de l'autre limite) au cap de R vers
  // L pris en R : tournée de la convergence des méridiens par rapport à la corde R–L, elle s'en écarte d'une dizaine
  // de mètres au bout d'une barre de 20 km (brigade), écart que la mesure reproduit pour rester sous le doigt.
  const bar = (position: number): [[number, number], [number, number]] => {
    const along = pointAlongPolyline(upper, position) ?? upper[upper.length - 1]!;
    const drawn = limaCrossing(along, upper, lower, 0);
    if (drawn) return [xy(drawn[2]), xy(drawn[0])];
    const r = xy(closestPoint(upper, along) ?? along);
    return [r, r];
  };
  const size = n + 1;
  const rx = new Float64Array(size);
  const ry = new Float64Array(size);
  const bx = new Float64Array(size);
  const by = new Float64Array(size);
  for (let k = 0; k < size; k++) {
    const [[x0, y0], [x1, y1]] = bar(track.low_m + k * step);
    [rx[k], ry[k], bx[k], by[k]] = [x0, y0, x1 - x0, y1 - y0];
  }
  // Sens du fuseau : la barre va toujours de la limite de référence à l'autre, si bien qu'une seule orientation de
  // la normale (à gauche ou à droite de la barre) vaut pour tout le fuseau ; c'est celle où avance le pied R, au
  // vote pondéré par son avance (un rebroussement de la limite de référence vote à rebours, sans l'emporter). La
  // normale suit ainsi la rotation de la barre, même d'un saut de plus de 90° : l'orienter d'après la normale
  // précédente la retournait à contresens sur un coude du fuseau de plus de 90°. Barre dégénérée (limites qui se
  // touchent) : écartée des bandes.
  let vote = 0;
  for (let k = 0; k < n; k++) {
    const len = Math.hypot(bx[k]!, by[k]!);
    if (len > 1) vote += (-by[k]! * (rx[k + 1]! - rx[k]!) + bx[k]! * (ry[k + 1]! - ry[k]!)) / len;
  }
  const sense = vote < 0 ? -1 : 1;
  const nx = new Float64Array(size);
  const ny = new Float64Array(size);
  const ok = new Uint8Array(size);
  for (let k = 0; k < size; k++) {
    const len = Math.hypot(bx[k]!, by[k]!);
    if (len > 1) [nx[k], ny[k], ok[k]] = [(sense * -by[k]!) / len, (sense * bx[k]!) / len, 1];
  }
  // Bandes : deux barres valides consécutives. Bande en triangle (le pied sur l'autre limite saute de plus de 4 pas
  // en un pas : le point le plus proche change de tronçon) : abscisses de part et d'autre du saut (`lo`, `hi`,
  // cherchées par dichotomie) et leurs barres.
  type Edge = { at: number; x: number; y: number; ex: number; ey: number; nx: number; ny: number };
  const bands: { k: number; j: number; jump: { lo: Edge; hi: Edge } | null }[] = [];
  const foot = (k: number): [number, number] => [rx[k]! + bx[k]!, ry[k]! + by[k]!];
  const edge = (at: number): Edge => {
    const [[x, y], [x1, y1]] = bar(at);
    const [ex, ey] = [x1 - x, y1 - y];
    const len = Math.hypot(ex, ey) || 1;
    return { at, x, y, ex, ey, nx: (sense * -ey) / len, ny: (sense * ex) / len };
  };
  for (let k = -1, j = 0; j < size; j++) {
    if (!ok[j]) continue;
    if (k >= 0) {
      const [[ax, ay], [cx, cy]] = [foot(k), foot(j)];
      let jump: { lo: Edge; hi: Edge } | null = null;
      if (Math.hypot(cx - ax, cy - ay) > 4 * step * (j - k)) {
        let [lo, hi] = [track.low_m + k * step, track.low_m + j * step];
        for (let i = 0; i < 24; i++) {
          const mid = (lo + hi) / 2;
          const [fx, fy] = bar(mid)[1];
          if (Math.hypot(fx - ax, fy - ay) <= Math.hypot(fx - cx, fy - cy)) lo = mid;
          else hi = mid;
        }
        jump = { lo: edge(lo), hi: edge(hi) };
      }
      bands.push({ k, j, jump });
    }
    k = j;
  }
  if (!bands.length) return fallback;
  const first = bands[0]!.k;
  const final = bands[bands.length - 1]!.j;
  const side = new Float64Array(size);
  let last = hint;
  let grabbed = false;
  return (point) => {
    const [px, py] = xy(point);
    for (let k = first; k <= final; k++) {
      if (!ok[k]) continue;
      side[k] = (px - rx[k]!) * nx[k]! + (py - ry[k]!) * ny[k]!;
    }
    // Côté du pointeur par rapport à une barre (orientée par sa normale) et distance à la LIMA prolongée.
    const sideOf = (e: Edge) => (px - e.x) * e.nx + (py - e.y) * e.ny;
    const gapTo = (e: Edge) => {
      const len2 = e.ex * e.ex + e.ey * e.ey;
      const reach = len2 > 0 ? margin / Math.sqrt(len2) : 0;
      const v = len2 > 0 ? Math.min(Math.max(((px - e.x) * e.ex + (py - e.y) * e.ey) / len2, -reach), 1 + reach) : 0;
      return Math.hypot(px - e.x - v * e.ex, py - e.y - v * e.ey);
    };
    const gapAt = (k: number) => {
      const len2 = bx[k]! * bx[k]! + by[k]! * by[k]!;
      const reach = margin / Math.sqrt(len2);
      const v = Math.min(Math.max(((px - rx[k]!) * bx[k]! + (py - ry[k]!) * by[k]!) / len2, -reach), 1 + reach);
      return Math.hypot(px - rx[k]! - v * bx[k]!, py - ry[k]! - v * by[k]!);
    };
    // Barre la plus proche du pointeur entre deux abscisses (section en trois, sur la barre du serveur), gardée si
    // elle fait mieux que celle de départ.
    const closestAround = (start: number, a: number, b: number): [number, number] => {
      [a, b] = [Math.max(a, track.low_m), Math.min(b, track.high_m)];
      for (let i = 0; i < 14 && b - a > 0.01; i++) {
        const [c, d] = [a + (b - a) / 3, b - (b - a) / 3];
        if (gapTo(edge(c)) <= gapTo(edge(d))) b = d;
        else a = c;
      }
      const [middle, here] = [(a + b) / 2, gapTo(edge(start))];
      const there = gapTo(edge(middle));
      return there < here ? [middle, there] : [start, here];
    };
    // Barres qui passent sous le pointeur : bandes où il change de côté, dans le sens du fuseau (σ = 1) ou à rebours
    // (σ = −1 : la limite de référence part de côté ou rebrousse chemin, les barres reculent). Bande en triangle :
    // seulement si le pointeur est dans le balayage continu avant le saut (`lo`) ou après (`hi`).
    type Crossing = { a: number; fa: number; b: number; fb: number; sigma: number };
    // Candidate : abscisse et distance de la LIMA prolongée au pointeur (celle d'une croisée, une fois affinée) ;
    // `firm` : barre qui passe sous le pointeur (croisée, ou LIMA saisie à moins d'1 m).
    type Candidate = { position: number; gap: number; crossing: Crossing | null; firm: boolean };
    const candidates: Candidate[] = [];
    const offer = (position: number, gap: number, crossing: Crossing | null = null, firm = crossing !== null) =>
      candidates.push({ position, gap, crossing, firm });
    // Saisie (premier appel) : la LIMA telle qu'elle est (`hint`) concourt, si bien que la mesure de la saisie rend sa
    // position quand elle passe sous le pointeur aussi bien qu'une autre barre (pivot autour d'un coin de l'autre
    // limite, où toutes les barres voisines passent sous le doigt).
    if (!grabbed && hint !== undefined && hint >= track.low_m && hint <= track.high_m) {
      const gap = gapTo(edge(hint));
      offer(hint, gap, null, gap <= 1);
    }
    grabbed = true;
    if (side[first]! < 0) offer(track.low_m + first * step, gapAt(first));
    if (side[final]! > 0) offer(track.low_m + final * step, gapAt(final));
    for (const { k, j, jump } of bands) {
      const [h0, h1] = [side[k]!, side[j]!];
      const sigma = h0 >= 0 && h1 <= 0 && h0 > h1 ? 1 : h0 <= 0 && h1 >= 0 && h1 > h0 ? -1 : 0;
      if (!sigma) continue;
      const u = h0 / (h0 - h1);
      const [p0, p1] = [track.low_m + k * step, track.low_m + j * step];
      if (!jump) offer(p0 + u * (p1 - p0), Infinity, { a: p0, fa: sigma * h0, b: p1, fb: sigma * h1, sigma });
      else {
        const [sLo, sHi] = [sigma * sideOf(jump.lo), sigma * sideOf(jump.hi)];
        if (sLo <= 0) offer(p0, Infinity, { a: p0, fa: sigma * h0, b: jump.lo.at, fb: sLo, sigma });
        else if (sHi >= 0) offer(p1, Infinity, { a: jump.hi.at, fa: sHi, b: p1, fb: sigma * h1, sigma });
        else {
          // Le pointeur est dans le triangle que la barre du serveur franchit d'un saut : aucune barre ne passe
          // dessous. La LIMA reste sur la barre la plus proche de part et d'autre du saut, et franchit le saut quand
          // le doigt passe à égale distance des deux. De chaque côté : la barre du saut, ou celle que l'on trouve en
          // remontant le balayage tant que la barre se rapproche du doigt (derrière un coin, les barres reculent
          // avant de sauter), si elle est nettement plus proche (d'un cinquième au moins : pas de va-et-vient d'un
          // pas pour quelques mètres loin des barres).
          const [gapLo, gapHi] = [gapTo(jump.lo), gapTo(jump.hi)];
          let [before, gapBefore] = [jump.lo.at, gapLo];
          for (let i = k; i >= first && ok[i] && gapAt(i) < gapBefore; i--)
            [before, gapBefore] = [track.low_m + i * step, gapAt(i)];
          [before, gapBefore] = closestAround(before, before - step, Math.min(before + step, jump.lo.at));
          if (gapBefore > 0.8 * gapLo) [before, gapBefore] = [jump.lo.at, gapLo];
          let [after, gapAfter] = [jump.hi.at, gapHi];
          for (let i = j; i <= final && ok[i] && gapAt(i) < gapAfter; i++)
            [after, gapAfter] = [track.low_m + i * step, gapAt(i)];
          [after, gapAfter] = closestAround(after, Math.max(after - step, jump.hi.at), after + step);
          if (gapAfter > 0.8 * gapHi) [after, gapAfter] = [jump.hi.at, gapHi];
          if (gapBefore <= gapAfter) offer(before, gapBefore);
          else offer(after, gapAfter);
        }
      }
    }
    // Abscisse exacte d'une croisée : l'interpolation entre deux barres distantes de 20 m est affinée sur la barre
    // du serveur elle-même (fausse position d'Illinois, encadrée par deux barres de part et d'autre du pointeur).
    const refine = ({ a, fa, b, fb, sigma }: Crossing): number => {
      let kept = 0;
      for (let i = 0; i < 12 && fa - fb > 0; i++) {
        const c = a + (fa / (fa - fb)) * (b - a);
        const fc = sigma * sideOf(edge(c));
        if (Math.abs(fc) < 0.01) [a, fa, b, fb] = [c, 0, c, 0];
        else if (fc > 0) {
          [a, fa] = [c, fc];
          if (kept === 1) fb /= 2;
          kept = 1;
        } else {
          [b, fb] = [c, fc];
          if (kept === -1) fa /= 2;
          kept = -1;
        }
      }
      const found = fa - fb > 0 ? a + (fa / (fa - fb)) * (b - a) : a;
      // Petit saut de la barre du serveur dans la bande (le pied change de tronçon de moins de 4 pas) : la fausse
      // position s'arrête sur le saut sans trouver de barre sous le pointeur ; la plus proche des deux bords.
      if (Math.abs(sideOf(edge(found))) <= 1) return found;
      return gapTo(edge(a)) <= gapTo(edge(b)) ? a : b;
    };
    // Choix entre plusieurs barres sous le pointeur, chacune à son abscisse exacte et mesurée par la distance de la
    // LIMA prolongée au pointeur : une croisée qui n'aboutit qu'au bord d'un saut, ou dont seule la droite passe par
    // le pointeur, compte pour ce qu'elle laisse d'écart.
    // Barre la plus proche sur la branche de la dernière abscisse (en descendant l'écart depuis elle, puis affinée
    // entre ses voisines) : elle passe sous le pointeur à moins d'1 m autour d'un pivot, où les croisées se
    // confondent et peuvent manquer ; ailleurs, elle rejoint la croisée de la branche ou s'en tient loin.
    if (last !== undefined) {
      let k = Math.min(Math.max(Math.round((last - track.low_m) / step), first), final);
      while (k > first && !ok[k]) k--;
      if (ok[k]) {
        for (let down = k - 1; down >= first && ok[down] && gapAt(down) < gapAt(k); down--) k = down;
        for (let up = k + 1; up <= final && ok[up] && gapAt(up) < gapAt(k); up++) k = up;
        const at = track.low_m + k * step;
        const [position, gap] = closestAround(at, at - step, at + step);
        offer(position, gap, null, gap <= 1);
      }
    }
    let firm = Infinity;
    for (const candidate of candidates) {
      if (candidate.crossing) {
        candidate.position = refine(candidate.crossing);
        candidate.gap = gapTo(edge(candidate.position));
      }
      if (candidate.firm) firm = Math.min(firm, candidate.gap);
    }
    // Une barre passe sous le pointeur (à 10 m près) : seules concourent celles qui y passent. Une barre voisine qui
    // ne fait qu'en approcher (première ou dernière barre devant laquelle est le pointeur, bord d'un saut) figerait
    // la LIMA autour d'un pivot, où toutes les barres passent à quelques mètres du doigt.
    const onBar = firm <= SLIDE_TIE_M;
    let nearest = onBar ? firm : Infinity;
    if (!onBar) for (const candidate of candidates) nearest = Math.min(nearest, candidate.gap);
    // Pointeur sous aucune barre (dans le triangle d'un saut, au-delà des prolongements, devant un éventail de barres
    // presque aussi proches les unes que les autres) : concourt aussi la barre la plus proche, affinée entre ses
    // voisines.
    if (!onBar) {
      let [closest, gapMin] = [-1, Infinity];
      for (let k = first; k <= final; k++) if (ok[k] && gapAt(k) < gapMin) [closest, gapMin] = [k, gapAt(k)];
      if (closest < 0 && !candidates.length) return fallback(point);
      if (closest >= 0) {
        const at = track.low_m + closest * step;
        const [position, gap] = closestAround(at, at - step, at + step);
        offer(position, gap);
        nearest = Math.min(nearest, gap);
      }
    }
    // Celles qui passent au plus près, à 10 m près plus un dixième de l'écart (toutes celles qui portent le pointeur ;
    // loin de toute barre, la moins éloignée et celles qui la suivent de peu), puis la plus proche de la dernière
    // abscisse rendue : sur le prolongement comme entre les limites, la LIMA ne change pas de branche sous le doigt.
    const tie = nearest + SLIDE_TIE_M + 0.1 * nearest;
    let best = NaN;
    for (const candidate of candidates) {
      if ((onBar && !candidate.firm) || candidate.gap > tie) continue;
      const closer = Number.isNaN(best)
        ? true
        : last === undefined
          ? candidate.position < best
          : Math.abs(candidate.position - last) < Math.abs(best - last);
      if (closer) best = candidate.position;
    }
    last = best;
    return best;
  };
}

/** Décalage du GDH (h, arrondi comme sur l'iPad) pour une ligne portée à l'abscisse `position`. */
export function previewOffset(corridor: Corridor, line: PhaseLine, position: number): number {
  const shift = position - (line.position_m - line.shift_m);
  const hours = (shift * corridor.hours_per_length) / corridor.track.length_m;
  return hours >= 0 ? Math.floor(hours + 0.5) : -Math.floor(-hours + 0.5);
}

interface Drawn {
  shape: CorridorShape;
  details: Partial<Record<string, string | null>>;
}

/** Géométrie et GDH affichés depuis le serveur (aperçu du glissement d'une LIMA du fuseau automatique compris). */
function serverDrawn(
  corridor: Corridor,
  preview: { name: string; position_m: number } | null,
  params: { start_dtg?: string | null; time_zone?: string | null } | null,
): Drawn {
  const details: Drawn["details"] = {};
  const lines = corridor.lines.map((line) => {
    // Aperçu pendant un glissement : la barre suit le pointeur le long du repère, GDH recalculé.
    const dragged = preview?.name === line.name;
    let detail = line.dtg;
    if (dragged) {
      const offset = previewOffset(corridor, line, preview.position_m);
      const stamp = params?.start_dtg
        ? formatDtg(params.start_dtg, line.hours - line.offset_h + offset, params.time_zone ?? null)
        : null;
      detail = stamp ?? `${offset > 0 ? "+" : offset < 0 ? "−" : "±"}${Math.abs(offset)} h`;
    }
    details[line.name] = detail;
    return { name: line.name, coords: dragged ? lineGeometry(corridor, preview.position_m) : line.coordinates };
  });
  return {
    shape: {
      upper: corridor.upper,
      lower: corridor.lower,
      lines,
      marks: corridor.marks.map((mark) => ({ at: mark.coordinates, rotation: mark.rotation })),
    },
    details,
  };
}

/** Géométrie et GDH d'aperçu depuis le modèle éditable (geste en cours, aperçu de l'historique). */
function modelDrawn(
  model: CorridorModel,
  params: { start_dtg?: string | null; time_zone?: string | null } | null,
): Drawn {
  const hours = modelHours(model);
  const details: Drawn["details"] = {};
  for (const [name, h] of Object.entries(hours)) {
    if (h === undefined) continue;
    details[name] = (params?.start_dtg ? formatDtg(params.start_dtg, h, params.time_zone ?? null) : null) ?? `${t("Début")} + ${h} h`;
  }
  return { shape: shapeFromModel(model), details };
}

const collection = (features: Feature[]) => ({ type: "FeatureCollection", features }) as FeatureCollection;

/** Limites et symboles d'échelon d'un tracé (indépendants du zoom). */
function railFeatures(shape: CorridorShape, echelon: string) {
  return {
    rails: collection(
      [shape.upper, shape.lower]
        .filter((coords) => coords.length >= 2)
        .map((coords) => ({
          type: "Feature",
          geometry: { type: "LineString", coordinates: coords },
          properties: {},
        })),
    ),
    marks: collection(
      shape.marks.map((mark) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: mark.at },
        properties: { text: echelon, rotation: mark.rotation },
      })),
    ),
  };
}

/** LIMA dessinées (prolongées selon le zoom), séparées des limites : un nouveau quart de niveau de zoom ne
 *  renvoie que cette source au moteur. */
function lineFeatures(lines: CorridorShape["lines"], lineOpacity: ShapeFrame["lineOpacity"] = {}) {
  return collection(
    lines.map((line) => ({
      type: "Feature",
      geometry: { type: "LineString", coordinates: line.coords },
      properties: { name: line.name, o: lineOpacity[line.name] ?? 1 },
    })),
  );
}

/** Peinture d'une couche pendant un remplacement animé : opacité imposée à chaque image, sans transition du
 *  moteur ; hors animation, le fondu d'ouverture du dossier (`revealPaint`). */
function animatedPaint(
  type: "line" | "symbol",
  paint: Record<string, unknown>,
  revealed: boolean,
  opacity: number | null,
): Record<string, unknown> {
  if (opacity === null) return revealPaint(type, paint, revealed);
  const keys = type === "line" ? ["line-opacity"] : ["icon-opacity", "text-opacity"];
  const out: Record<string, unknown> = { ...paint };
  for (const key of keys) {
    out[key] = (revealed ? ((paint[key] as number | undefined) ?? 1) : 0) * opacity;
    out[`${key}-transition`] = { duration: 0, delay: 0 };
  }
  return out;
}

function tagStyle(revealed: boolean, opacity: number | null): CSSProperties {
  if (opacity === null) return revealStyle(revealed, REVEAL_MS);
  return { opacity: revealed ? opacity : 0 };
}

export function CorridorLayers({ Source, Layer, Marker, zoom, bearing = 0 }: EngineParts) {
  const { data } = useShownDeployment(); // aperçu du sens de la manœuvre (docs/14 §2.14)
  const visible = useUi((s) => s.controlVisible);
  const revealed = useMapRevealed();
  const preview = useUi((s) => s.linePreview);
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const editing = useManoeuvre((s) => s.editing);
  const working = useManoeuvre((s) => s.working);
  // Aperçu du sens de la manœuvre : le fuseau suit la flèche sans remplacement animé.
  const orienting = useOrientation((s) => s.open);
  const historyPreview = useGestureState((s) => s.historyPreview);
  const language = useDossierLanguage();
  const lineLabel = (name: string) => lineLabelIn(name, language);
  const corridor = data?.corridor ?? null;
  // Zone libre en préparation : limites en tirets (prévu, APP-6) ; zone prête : fondu enchaîné vers le trait plein.
  const { zone } = useOpenZone();
  const planned = useEased(zone && zone.status !== "ready" ? 1 : 0, DATA_TRANSITION_MS);

  // Plusieurs ⌘Z rapides : le fuseau et les LIMA visés suivent tout de suite, avant la réponse (docs/14 §2.8).
  useEffect(() => {
    setDispositifPreview((state) => useGestureState.getState().setHistoryPreview(state?.corridor ?? null));
    return () => {
      setDispositifPreview(null);
      useGestureState.getState().setHistoryPreview(null);
    };
  }, []);

  const model = working ?? historyPreview;
  const target = useMemo<Drawn | null>(() => {
    if (model && model.left.length >= 2) return modelDrawn(model, params);
    return corridor ? serverDrawn(corridor, preview, params) : null;
  }, [model, corridor, preview, params]);

  // Remplacement animé : la géométrie affichée part de ce qui est à l'écran. Pas d'animation pendant un geste
  // (l'aperçu suit le pointeur), à l'ouverture d'un dossier, ni en mouvement réduit.
  const shown = useRef<Drawn | null>(null);
  const previous = useRef<{ target: Drawn | null; dossier: string | null }>({ target: null, dossier: null });
  const animation = useRef<{ from: Drawn; to: Drawn; started: number } | null>(null);
  if (previous.current.target !== target) {
    const from = shown.current;
    const direct =
      !from ||
      !target ||
      working !== null ||
      orienting ||
      preview !== null ||
      !revealed ||
      previous.current.dossier !== exerciseId ||
      !canAnimate() ||
      sameShape(from.shape, target.shape);
    animation.current = direct ? null : { from, to: target, started: performance.now() };
    previous.current = { target, dossier: exerciseId };
  }
  const now = useTicker(animation.current !== null);
  let frame: ShapeFrame | null = null;
  if (animation.current && target) {
    const progress = Math.min(1, Math.max(0, (now - animation.current.started) / DATA_TRANSITION_MS));
    frame = blendShapes(animation.current.from.shape, target.shape, smoothstep(progress));
    if (progress >= 1) {
      animation.current = null;
      frame = null;
    }
  }
  const drawnShape = frame?.shape ?? target?.shape ?? null;
  if (target && drawnShape) shown.current = { shape: drawnShape, details: target.details };
  const ghost = frame?.ghost ? { shape: frame.ghost.shape, opacity: frame.ghost.opacity } : null;
  const ghostDetails = animation.current?.from.details ?? {};
  // Remplacement animé : limites et symboles à `railsOpacity` (1 s'ils glissent), LIMA une à une (`o` de chaque
  // tracé : 1 si elle glisse, l'avancement si elle apparaît en fondu).
  const railsOpacity = frame ? frame.railsOpacity : null;
  const lineOpacity = frame?.lineOpacity ?? null;
  const linesFading = lineOpacity !== null && Object.keys(lineOpacity).length > 0;

  // Dépassement visible des LIMA au-delà des limites (affichage seulement) : tracé prolongé si les 500 m des
  // données font moins de `minPx` au zoom courant, arrondi au quart de niveau inférieur ; hors édition, au plus 5 % de
  // la largeur locale du fuseau, camps compris (fuseau vu de loin : l'aspect d'un camp à son zoom d'entrée).
  const minPx = editing ? LIMA_OVERSHOOT_EDIT_PX : LIMA_OVERSHOOT_PX;
  const widthRatio = limaOvershootRatio(editing);
  const atZoom = overshootZoom(zoom);
  const extended = useMemo(
    () => (drawnShape ? overshootLines(drawnShape.lines, [drawnShape.upper, drawnShape.lower], minPx, atZoom, widthRatio) : null),
    [drawnShape, minPx, atZoom, widthRatio],
  );
  const ghostShape = ghost?.shape ?? null;
  const ghostExtended = useMemo(() => {
    if (!ghostShape) return null;
    // Limites qui glissent : absentes de l'image qui s'efface, celles du fuseau dessiné servent de repère.
    const rails = ghostShape.upper.length >= 2 ? ghostShape : drawnShape;
    const boundaries = rails ? [rails.upper, rails.lower] : [];
    return overshootLines(ghostShape.lines, boundaries, minPx, atZoom, widthRatio);
  }, [ghostShape, drawnShape, minPx, atZoom, widthRatio]);

  const echelon = corridor?.echelon ?? "";
  const mainRails = useMemo(() => (drawnShape ? railFeatures(drawnShape, echelon) : null), [drawnShape, echelon]);
  const mainLines = useMemo(() => (extended ? lineFeatures(extended, lineOpacity ?? {}) : null), [extended, lineOpacity]);
  const fadedRails = useMemo(() => (ghostShape ? railFeatures(ghostShape, echelon) : null), [ghostShape, echelon]);
  const fadedLines = useMemo(() => (ghostExtended ? lineFeatures(ghostExtended) : null), [ghostExtended]);
  const faded = fadedRails && fadedLines ? { ...fadedRails, lines: fadedLines } : null;
  const main = mainRails && mainLines ? { ...mainRails, lines: mainLines } : null;

  // Étiquettes sans recouvrement (au quart de niveau de zoom et au cap de la carte), publiées avec la géométrie
  // affichée pour les lignes de la ZO.
  const limaShown = visible.lima || editing;
  const details = target?.details;
  const chips = useMemo(() => {
    if (!corridor || !drawnShape || !extended || !limaShown) return [];
    return stackLimaChips(
      extended.map((line) => ({
        name: line.name,
        coords: line.coords,
        label: lineLabelIn(line.name, language),
        detail: details?.[line.name] ?? null,
      })),
      { zoom: atZoom, bearing, rails: [drawnShape.upper, drawnShape.lower], marks: drawnShape.marks.map((mark) => mark.at) },
    );
  }, [corridor, drawnShape, extended, limaShown, language, details, atZoom, bearing]);
  const setLimaGeometry = useLimaGeometry((s) => s.setGeometry);
  useEffect(() => {
    if (!corridor || !drawnShape || !extended) {
      setLimaGeometry(EMPTY_LIMA_GEOMETRY);
      return;
    }
    setLimaGeometry({
      chips,
      lines: limaShown ? extended.map((line) => line.coords) : [],
      rails: [drawnShape.upper, drawnShape.lower],
      marks: drawnShape.marks.map((mark) => mark.at),
    });
  }, [corridor, drawnShape, extended, limaShown, chips, setLimaGeometry]);
  useEffect(() => () => setLimaGeometry(EMPTY_LIMA_GEOMETRY), [setLimaGeometry]);

  if (!corridor || !main || !extended) return null;
  // En édition, le fuseau et ses LIMA restent affichés : les poignées s'y posent.
  const railsVisibility = visible.fuseau || editing ? "visible" : "none";
  const limaVisibility = limaShown ? "visible" : "none";
  // Étiquette au-delà de l'extrémité prolongée, dans l'axe de la ligne : le dépassement reste visible.
  // Repère ancré au centre (jamais recréé) ; la puce est poussée hors de la ligne par une transformation CSS
  // continue avec la direction de la ligne à l'écran. Étiquettes de ce qui s'efface : sans anti-recouvrement.
  const labels = (lines: CorridorShape["lines"]) =>
    lineLabels(lines).map((label) => ({ ...label, transform: labelTransform(labelPlacement(label.bearing, bearing)) }));

  return (
    <>
      {/* Ce qui s'efface pendant un remplacement : monté en cours de route, donc rangé sous le fuseau affiché
          (`beforeId`) plutôt qu'au-dessus des unités. */}
      {faded && ghost && (
        <>
          <Source id="gx-corridor-ghost-rails" type="geojson" data={faded.rails}>
            <Layer
              id="corridor-ghost-rails"
              beforeId="corridor-rails"
              type="line"
              layout={{ visibility: railsVisibility }}
              paint={animatedPaint("line", { "line-color": CONTROL_COLOR, "line-width": LINE_WIDTH }, revealed, ghost.opacity)}
            />
          </Source>
          <Source id="gx-corridor-ghost-marks" type="geojson" data={faded.marks}>
            <Layer
              id="corridor-ghost-echelon"
              beforeId="corridor-rails"
              type="symbol"
              layout={{ ...ECHELON_LAYOUT, visibility: railsVisibility }}
              paint={animatedPaint("symbol", { "text-color": CONTROL_COLOR }, revealed, ghost.opacity)}
            />
          </Source>
          <Source id="gx-corridor-ghost-lines" type="geojson" data={faded.lines}>
            <Layer
              id="corridor-ghost-lines"
              beforeId="corridor-rails"
              type="line"
              layout={{ visibility: limaVisibility }}
              paint={animatedPaint("line", { "line-color": LIMA_COLOR, "line-width": LINE_WIDTH }, revealed, ghost.opacity)}
            />
          </Source>
        </>
      )}
      <Source id="gx-corridor-rails" type="geojson" data={main.rails}>
        <Layer
          id="corridor-rails"
          type="line"
          layout={{ visibility: railsVisibility }}
          paint={animatedPaint(
            "line",
            { "line-color": CONTROL_COLOR, "line-width": LINE_WIDTH },
            revealed,
            planned > 0 ? (railsOpacity ?? 1) * (1 - planned) : railsOpacity,
          )}
        />
        {/* Zone libre en préparation : les mêmes limites en tirets, en fondu enchaîné avec le trait plein. */}
        <Layer
          id="corridor-rails-planned"
          type="line"
          layout={{ visibility: planned > 0 ? railsVisibility : "none" }}
          paint={animatedPaint(
            "line",
            { "line-color": CONTROL_COLOR, "line-width": LINE_WIDTH, "line-dasharray": [3, 2] },
            revealed,
            (railsOpacity ?? 1) * planned,
          )}
        />
      </Source>
      <Source id="gx-corridor-marks" type="geojson" data={main.marks}>
        <Layer
          id="corridor-rails-echelon"
          type="symbol"
          layout={{ ...ECHELON_LAYOUT, visibility: railsVisibility }}
          paint={animatedPaint("symbol", { "text-color": CONTROL_COLOR }, revealed, railsOpacity)}
        />
      </Source>
      <Source id="gx-corridor-lines" type="geojson" data={main.lines}>
        <Layer
          id="corridor-lines"
          type="line"
          layout={{ visibility: limaVisibility }}
          paint={
            linesFading
              ? {
                  "line-color": LIMA_COLOR,
                  "line-width": LINE_WIDTH,
                  "line-opacity": ["coalesce", ["get", "o"], 1],
                  "line-opacity-transition": { duration: 0, delay: 0 },
                }
              : animatedPaint("line", { "line-color": LIMA_COLOR, "line-width": LINE_WIDTH }, revealed, railsOpacity === null ? null : 1)
          }
        />
        {/* Couche de préhension, invisible et large : une ligne se saisit sans viser au pixel près. */}
        <Layer
          id={LINE_HIT_LAYER_ID}
          type="line"
          layout={{ visibility: limaVisibility }}
          paint={revealPaint("line", { "line-color": "#000000", "line-width": 18, "line-opacity": 0.01 }, revealed)}
        />
      </Source>
      {limaShown &&
        ghost &&
        ghostExtended &&
        labels(ghostExtended).map((label) => (
          <Marker
            key={`ghost-${label.name}`}
            longitude={label.at[0]}
            latitude={label.at[1]}
            anchor="center"
            style={{ pointerEvents: "none" }}
          >
            <div style={{ ...tagStyle(revealed, ghost.opacity), transform: label.transform }}>
              <LineTag name={lineLabel(label.name)} detail={ghostDetails[label.name] ?? null} />
            </div>
          </Marker>
        ))}
      {limaShown &&
        chips.map((chip) => (
          <Marker
            key={chip.id}
            longitude={chip.at[0]}
            latitude={chip.at[1]}
            anchor="center"
            style={{ pointerEvents: "none" }}
          >
            {/* Fondu sur le contenu : le moteur réécrit l'opacité de l'élément du repère (occultation). */}
            <div
              style={{
                ...tagStyle(revealed, lineOpacity === null ? null : (lineOpacity[chip.id as LineName] ?? 1)),
                transform: `translate(${chip.offsetPx[0]}px, ${chip.offsetPx[1]}px) ${labelTransform(labelPlacement(chip.bearing, bearing))}`,
              }}>
              <LineTag name={lineLabel(chip.id)} detail={target?.details[chip.id] ?? null} tag={chip.id} />
            </div>
          </Marker>
        ))}
    </>
  );
}
